// ImpersonationAudit — who signed in as whom, and when (ticket 205).
// GET /api/super/audit is owner-only and returns the latest 200 events; the
// host page (SuperAdminPage) already gates on is_super_admin.
import React, { useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import { api } from '../api';
import { errorMessage } from '../errors';
import { formatDateTime } from '../kit/time';
import { TableScroller } from '@/components/ui/table-scroller';

const KIND_LABELS = { tenant_admin: 'Signed in as admin', borrower_preview: 'Borrower portal preview' };

export default function ImpersonationAudit() {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/super/audit')
      .then(({ data }) => setEvents(data.events || []))
      .catch((e) => setError(errorMessage(e, 'Could not load the impersonation log')));
  }, []);

  return (
    <section className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden mb-8 text-[13px]" data-testid="impersonation-audit">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className="p-2 rounded-lg" style={{ background: '#070B5F15', color: '#070B5F' }}>
          <History size={16} />
        </div>
        <div>
          <h2 className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Impersonation log</h2>
          <div className="text-[11px] text-slate-500">The latest 200 sign-ins as a tenant admin or borrower preview.</div>
        </div>
      </div>

      {error && <div className="p-6 text-red-800" data-testid="impersonation-audit-error">{error}</div>}
      {!error && events === null && (
        <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      )}
      {!error && events && events.length === 0 && (
        <div className="p-6 text-slate-500 italic" data-testid="impersonation-audit-empty">No impersonation yet.</div>
      )}
      {!error && events && events.length > 0 && (
        <TableScroller><table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-100">
              <th className="text-left px-5 py-2">When</th>
              <th className="text-left px-2 py-2">Who</th>
              <th className="text-left px-2 py-2">Action</th>
              <th className="text-left px-2 py-2">Tenant</th>
              <th className="text-left px-5 py-2">Signed in as</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.audit_id} className="border-b border-slate-50 last:border-0" data-testid={`impersonation-row-${ev.audit_id}`}>
                <td className="px-5 py-2.5 text-slate-500 whitespace-nowrap">{formatDateTime(ev.at)}</td>
                <td className="px-2 py-2.5">{ev.actor_email || ev.actor_user_id}</td>
                <td className="px-2 py-2.5 whitespace-nowrap">{KIND_LABELS[ev.kind] || ev.kind}</td>
                <td className="px-2 py-2.5" style={{ color: '#070B5F' }}>{ev.tenant_name || ev.tenant_id}</td>
                <td className="px-5 py-2.5">{ev.target_email || ev.target_name || ev.target_user_id}</td>
              </tr>
            ))}
          </tbody>
        </table></TableScroller>
      )}
    </section>
  );
}
